"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

const BUILD_ID_ENDPOINT = "/api/build-id";
const STALE_BUILD_STORAGE_KEY = "staleBuildReloaded";

/**
 * Detects when the client is running a stale build (e.g. after a deployment)
 * and triggers a reload before the user hits a ChunkLoadError.
 *
 * On first mount, it fetches and stores the current build ID.
 * On each client-side navigation, it re-checks the server's build ID.
 * If a mismatch is found, it reloads the page once (guarded by sessionStorage
 * to prevent loops).
 */
export function useStaleBuildDetection() {
  const pathname = usePathname();
  const clientBuildId = useRef<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetch(BUILD_ID_ENDPOINT, { signal: controller.signal })
      .then((res) => res.json())
      .then((data: { buildId: string }) => {
        if (!clientBuildId.current) {
          // First load — store the build ID this client was served with
          clientBuildId.current = data.buildId;
          sessionStorage.removeItem(STALE_BUILD_STORAGE_KEY);
          return;
        }

        if (
          data.buildId !== clientBuildId.current &&
          data.buildId !== "development"
        ) {
          // Build mismatch detected — reload once to get fresh chunks
          const alreadyReloaded = sessionStorage.getItem(
            STALE_BUILD_STORAGE_KEY,
          );

          if (!alreadyReloaded) {
            sessionStorage.setItem(STALE_BUILD_STORAGE_KEY, "true");
            window.location.reload();
          }
        }
      })
      .catch(() => {
        // Silently ignore — network errors shouldn't break the app
      });

    return () => controller.abort();
  }, [pathname]);
}
